import { Component, effect, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { error, isBlinkBrowser, isFirefoxBrowser } from '@4players/odin-common';
import { AppStore } from '../../../../state/app.repository';
import { DeviceService } from '../../../../core/services/device.service';
import {
  camErrorMessage,
  LocalStreamService,
} from '../../../../core/services/local-stream.service';
import { MediaPermissionsService } from '../../../../core/services/media-permissions.service';
import { UserService } from '../../../../core/services/user.service';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from '../../../../shared/components/ui/buttons/gradient-btn/button';
import { SelectField } from '../../../../shared/components/form-fields/select-field/select-field';
import { TranslatePipe } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconBtn } from '../../../../shared/components/ui/buttons/icon-btn/icon-btn';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { faCameraMovie, faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';
import { MediaAccessDenied } from '../../../../shared/components/ui/media-access-denied/media-access-denied';
import { MediaAccessPrompt } from '../../../../shared/components/ui/media-access-prompt/media-access-prompt';
import { Notice } from '../../../../shared/components/ui/notice/notice';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSliderWrapper } from '../../../../shared/components/material-wrappers/mat-slider-wrapper/mat-slider-wrapper';
import { MatToggleWrapper } from '../../../../shared/components/material-wrappers/mat-toggle-wrapper/mat-toggle-wrapper';
import { MediaConfigStore } from '../../../../state/media-config.repository';
import { BackgroundBlurService } from '../../../../core/services/background-blur.service';
import { config } from '../../../../../environments/environment';
import {
  DEFAULT_CAMERA_SETTINGS,
  DEFAULT_SCREEN_SETTINGS,
  VALID_BITRATES,
  VALID_CODECS,
  VALID_FRAMERATES,
  VALID_RESOLUTIONS,
  VALID_DEGRADATION_MODES,
  VideoSettings,
} from '../../../../shared/constants/video-settings';
import { Stepper } from '../../../../shared/components/ui/stepper/stepper';
import { Tab, Tabs } from '../../../../shared/components/ui/tabs/tabs';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { NativeVideoPipe } from '../../../../shared/pipes/native-video.pipe';
import { VideoMountDirective } from '../../../../shared/directives/video-mount.directive';
import { isTauri } from '../../../../shared/helpers/platform';
import { screenShareSupported } from '../../../../core/services/local-stream.service';

@Component({
  selector: 'andor-settings-video',
  standalone: true,
  imports: [
    NativeVideoPipe,
    VideoMountDirective,
    AsyncPipe,
    FormsModule,
    Button,
    SelectField,
    TranslatePipe,
    FaIconComponent,
    IconBtn,
    MediaAccessDenied,
    MediaAccessPrompt,
    Notice,
    MatSlider,
    MatSliderThumb,
    MatSlideToggle,
    MatSliderWrapper,
    MatToggleWrapper,
    Stepper,
    Tabs,
    TooltipDirective,
  ],
  templateUrl: './settings-video.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './settings-video.scss',
})
export class SettingsVideo {
  isPreviewDialog = input(false);
  allowCameraTest = input(true);
  autoStartCamera = input(false);
  direction = input<'top' | 'bottom' | 'auto'>('auto');

  isChromiumBrowser = isBlinkBrowser();
  backgroundBlurEnabled = signal(true);
  backgroundBlurAmount = signal(20);
  isBackgroundBlurSupported = BackgroundBlurService.isPlatformSupported();
  // Blur runs on MediaPipe in the browser; the native capture has no such stage.
  protected readonly blurAvailable = !isTauri;
  protected readonly screenShareSupported = screenShareSupported;
  showCamPreview = signal(false);
  showCameraPreview = signal(true);

  // Override settings
  overrideCameraSettings = signal(false);
  overrideScreenSettings = signal(false);
  cameraSettings = signal<VideoSettings>({ ...DEFAULT_CAMERA_SETTINGS });
  screenSettings = signal<VideoSettings>({ ...DEFAULT_SCREEN_SETTINGS });

  // Constants for template
  readonly validBitrates = [...VALID_BITRATES];
  readonly validFramerates = [...VALID_FRAMERATES];
  readonly resolutionTabs: Tab[] = VALID_RESOLUTIONS.map((r) => ({ id: r.label, label: r.label }));
  readonly codecTabs: Tab[] = VALID_CODECS.map((c) => ({ id: c, label: c }));
  readonly degradationTabs: Tab[] = VALID_DEGRADATION_MODES.map((m) => ({
    id: m.value,
    label: m.label,
  }));

  constructor(
    public appStore: AppStore,
    public deviceService: DeviceService,
    public localStream: LocalStreamService,
    public permissions: MediaPermissionsService,
    private _mediaConfig: MediaConfigStore,
    private _userService: UserService
  ) {
    this.backgroundBlurEnabled.set(_mediaConfig.value.backgroundBlurEnabled);
    this.backgroundBlurAmount.set(_mediaConfig.value.backgroundBlurAmount);
    this.showCameraPreview.set(_mediaConfig.value.showCameraPreview);

    // Initialize overrides from store
    const camOverride = _mediaConfig.value.cameraSettingsOverride;
    if (camOverride) {
      this.overrideCameraSettings.set(true);
      this.cameraSettings.set(camOverride);
    }
    const screenOverride = _mediaConfig.value.screenSettingsOverride;
    if (screenOverride) {
      this.overrideScreenSettings.set(true);
      this.screenSettings.set(screenOverride);
    }

    effect(() => {
      if (this.autoStartCamera() && this.permissions.cameraAccess() === 'granted') {
        this.startCam().then();
      }
    });

    effect(async () => {
      const status = permissions.cameraAccess();
      if (status === 'granted') {
        if (isFirefoxBrowser()) {
          await permissions.requestUserMedia('video').catch(error);
        }
        deviceService.refreshVideoDevices().then();
      }
    });

    // Self-heal the no-device error: when a camera is plugged in while the
    // error is displayed, retry automatically instead of waiting for a
    // click. Deliberately scoped to this component's lifetime so a later
    // plug-in never silently starts the camera without visible preview UI.
    effect((onCleanup) => {
      if (this.localStream.camError()?.kind !== 'no-device') return;
      const retry = () => this.startCam();
      navigator.mediaDevices.addEventListener('devicechange', retry);
      onCleanup(() => navigator.mediaDevices.removeEventListener('devicechange', retry));
    });
  }

  get showTestComponents(): boolean {
    return config.showTestComponents;
  }

  updateShowCameraPreview() {
    const newValue = !this.showCameraPreview();
    this.showCameraPreview.set(newValue);
    this._mediaConfig.update({ showCameraPreview: newValue });
  }

  updateBackgroundBlurEnabled() {
    const newValue = !this.backgroundBlurEnabled();
    this.backgroundBlurEnabled.set(newValue);
    this._mediaConfig.update({ backgroundBlurEnabled: newValue });
  }

  updateBackgroundBlurAmount(value: number) {
    this.backgroundBlurAmount.set(value);
    this._mediaConfig.update({ backgroundBlurAmount: value });
  }

  ngOnDestroy(): void {
    if (!this.isPreviewDialog()) {
      this.stopCam();
    }
  }

  async requestCam() {
    this.permissions.requestCam().catch(error);
  }

  protected readonly camErrorMessage = camErrorMessage;

  async startCam() {
    this.showCamPreview.set(true);
    try {
      await this.localStream.startCamStream();
    } catch {
      // Surfaced in the preview area via localStream.camError.
    }
  }

  stopCam() {
    this.showCamPreview.set(false);
    if (!this._userService.ownUser()?.camStream) {
      this.localStream.stopCamStream('settings video');
    }
  }

  // Override helpers
  toggleCameraOverride() {
    const enabled = !this.overrideCameraSettings();
    this.overrideCameraSettings.set(enabled);
    if (enabled) {
      this._mediaConfig.update({ cameraSettingsOverride: this.cameraSettings() });
    } else {
      this.cameraSettings.set({ ...DEFAULT_CAMERA_SETTINGS });
      this._mediaConfig.update({ cameraSettingsOverride: null });
    }
  }

  toggleScreenOverride() {
    const enabled = !this.overrideScreenSettings();
    this.overrideScreenSettings.set(enabled);
    if (enabled) {
      this._mediaConfig.update({ screenSettingsOverride: this.screenSettings() });
    } else {
      this.screenSettings.set({ ...DEFAULT_SCREEN_SETTINGS });
      this._mediaConfig.update({ screenSettingsOverride: null });
    }
  }

  updateCameraSetting(patch: Partial<VideoSettings>) {
    const updated = { ...this.cameraSettings(), ...patch } as VideoSettings;
    this.cameraSettings.set(updated);
    this._mediaConfig.update({ cameraSettingsOverride: updated });
  }

  updateScreenSetting(patch: Partial<VideoSettings>) {
    const updated = { ...this.screenSettings(), ...patch } as VideoSettings;
    this.screenSettings.set(updated);
    this._mediaConfig.update({ screenSettingsOverride: updated });
  }

  getCodecTab(settings: VideoSettings): Tab {
    return { id: settings.codec, label: settings.codec };
  }

  onCodecChange(tab: Tab, type: 'camera' | 'screen') {
    const codec = tab.id as VideoSettings['codec'];
    if (type === 'camera') {
      this.updateCameraSetting({ codec });
    } else {
      this.updateScreenSetting({ codec });
    }
  }

  getResolutionTab(settings: VideoSettings): Tab {
    const match = VALID_RESOLUTIONS.find(
      (r) =>
        r.value.width === settings.resolution.width && r.value.height === settings.resolution.height
    );
    return { id: match?.label ?? '', label: match?.label ?? '' };
  }

  getDegradationTab(settings: VideoSettings): Tab {
    const match = VALID_DEGRADATION_MODES.find((m) => m.value === settings.degradationPreference);
    return { id: match?.value ?? '', label: match?.label ?? '' };
  }

  onResolutionChange(tab: Tab, type: 'camera' | 'screen') {
    const res = VALID_RESOLUTIONS.find((r) => r.label === tab.label);
    if (!res) return;
    if (type === 'camera') {
      this.updateCameraSetting({ resolution: { ...res.value } });
    } else {
      this.updateScreenSetting({ resolution: { ...res.value } });
    }
  }

  onDegradationChange(tab: Tab, type: 'camera' | 'screen') {
    const mode = VALID_DEGRADATION_MODES.find((m) => m.value === tab.id);
    if (!mode) return;
    if (type === 'camera') {
      this.updateCameraSetting({ degradationPreference: mode.value });
    } else {
      this.updateScreenSetting({ degradationPreference: mode.value });
    }
  }

  protected readonly faClose = faClose;
  protected readonly faCameraMovie = faCameraMovie;
  protected readonly faInfoCircle = faInfoCircle;
}
